import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type PaymentMethod = 'card' | 'pix' | 'boleto';

export function usePaymentPreference(companyId?: string) {
  const queryClient = useQueryClient();
  
  // Buscar preferência atual do banco
  const { data: preference = 'boleto', isLoading } = useQuery({
    queryKey: ['payment-preference', companyId],
    queryFn: async () => {
      if (!companyId) return 'boleto';
      
      const { data, error } = await supabase
        .from('companies')
        .select('preferred_payment_method')
        .eq('id', companyId)
        .single();
      
      if (error) throw error;
      return (data?.preferred_payment_method || 'boleto') as PaymentMethod;
    },
    enabled: !!companyId,
  });

  // Mutation para atualizar preferência
  const updatePreference = useMutation({
    mutationFn: async (method: PaymentMethod) => {
      if (!companyId) throw new Error('Company ID is required');
      
      const { error } = await supabase
        .from('companies')
        .update({ preferred_payment_method: method })
        .eq('id', companyId);
      
      if (error) throw error;
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-preference', companyId] });
      toast.success('Método de pagamento atualizado');
    },
    onError: (error) => {
      console.error('Error updating payment preference:', error);
      toast.error('Erro ao atualizar método de pagamento');
    },
  });

  return {
    preference,
    isLoading,
    updatePreference,
  };
}

export default usePaymentPreference;
